import i18n from '../i18n';
import { apiRequest } from './http';
import { MAX_UPLOAD_FILE_SIZE_BYTES } from './templates';
import type { SlideDeckUploadOptions, SlideLibraryFilter, SlideLibraryItem, SplitSlideDeckResult } from './types';

const slideLibraryPath = '/api/manage/slide-library';

// SWA enforces a hard 30 MB request-size limit at the platform edge (independent of the API's
// own Kestrel body-size config, which only applies to local dev), so a real deck above ~22 MB
// blows a single-shot base64 POST. Uploads are sent in chunks comfortably under that limit.
const CHUNK_SIZE_BYTES = 15 * 1024 * 1024;

/** The countries CountryNames.cs (api/Integrations) normalises free-form client data onto - kept in step with it. */
export const SLIDE_LIBRARY_COUNTRIES = [
  'Australia',
  'Austria',
  'Belgium',
  'Denmark',
  'Egypt',
  'France',
  'Germany',
  'Greece',
  'India',
  'Ireland',
  'Italy',
  'Netherlands',
  'New Zealand',
  'Norway',
  'Poland',
  'South Africa',
  'Spain',
  'Sweden',
  'Switzerland',
  'United Kingdom',
  'United States'
];

export const SLIDE_LIBRARY_LANGUAGES = ['English', 'Dutch'];

/** ISO 3166-1 alpha-2 codes for SLIDE_LIBRARY_COUNTRIES, kept in step with api/Integrations/IsoCodes.cs. */
export const COUNTRY_CODES: Record<string, string> = {
  Australia: 'AU',
  Austria: 'AT',
  Belgium: 'BE',
  Denmark: 'DK',
  Egypt: 'EG',
  France: 'FR',
  Germany: 'DE',
  Greece: 'GR',
  India: 'IN',
  Ireland: 'IE',
  Italy: 'IT',
  Netherlands: 'NL',
  'New Zealand': 'NZ',
  Norway: 'NO',
  Poland: 'PL',
  'South Africa': 'ZA',
  Spain: 'ES',
  Sweden: 'SE',
  Switzerland: 'CH',
  'United Kingdom': 'GB',
  'United States': 'US'
};

/** ISO 639-1 alpha-2 codes for SLIDE_LIBRARY_LANGUAGES, kept in step with api/Integrations/IsoCodes.cs. */
export const LANGUAGE_CODES: Record<string, string> = {
  English: 'EN',
  Dutch: 'NL'
};

/** Mirrors api/Auth/UserContext.cs's LanguageFor, so the upload default matches what generation would pick. */
export function languageForCountry(country: string): string {
  return country === 'Netherlands' ? 'Dutch' : 'English';
}

/** Wizard-facing: backs the SlideCollection picker. Also used by Studio's browse/filter UI. */
export async function listSlideLibraryItems(filter?: SlideLibraryFilter): Promise<SlideLibraryItem[]> {
  const params = new URLSearchParams();
  if (filter?.country) params.set('country', filter.country);
  if (filter?.language) params.set('language', filter.language);
  if (filter?.deckName) params.set('deckName', filter.deckName);
  if (filter?.rcOfficial !== undefined) params.set('rcOfficial', String(filter.rcOfficial));

  const query = params.toString();
  const response = await apiRequest<{ items?: SlideLibraryItem[] }>(`${slideLibraryPath}/browse${query ? `?${query}` : ''}`);
  return response.items ?? [];
}

function readSliceAsBase64(slice: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(reader.error?.message ?? 'Unable to read file'));
    reader.onload = () => {
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(slice);
  });
}

/** Studio-only: splits an uploaded multi-slide deck and pushes each slide into the library. */
export async function splitSlideLibraryDeck(file: File, options: SlideDeckUploadOptions): Promise<SplitSlideDeckResult> {
  if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
    throw new Error(i18n.t('errors.fileTooLarge', { maxSizeMb: MAX_UPLOAD_FILE_SIZE_BYTES / (1024 * 1024) }));
  }

  const uploadId = crypto.randomUUID();
  const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE_BYTES));

  let result: SplitSlideDeckResult | undefined;
  for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
    const start = chunkIndex * CHUNK_SIZE_BYTES;
    const chunkBase64 = await readSliceAsBase64(file.slice(start, start + CHUNK_SIZE_BYTES));
    result = await apiRequest<SplitSlideDeckResult>(`${slideLibraryPath}/split`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        uploadId,
        chunkIndex,
        totalChunks,
        fileName: file.name,
        chunkBase64,
        deckName: options.deckName,
        country: options.country,
        language: options.language
      })
    });
  }

  return result!;
}
